import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import type { userState } from "../../interface/MainInterface";
import { HttpMonitoringApi } from "../../interface/monitoring-api";
import { buildMonitoringDetailUrl } from "./monitoringDashboardUtils";
import {
  buildSentimentKeywordConfigFromMonitoring,
  mergeSentimentKeywordLists,
  normalizeSentimentKeywordText,
  removeSentimentKeyword,
  upsertSentimentKeyword,
  type SentimentKeywordMode,
} from "./monitoringSentimentKeywordUtils";
import { normalizeExcludeTerms } from "./monitoringExcludeTermUtils";
import {
  buildSearchTermRefsFromMonitoring,
  getSearchTermIds,
  getSearchTermNames,
  mapSearchTermOption,
  mergeSearchTermOptions,
  mergeSearchTermRefs,
  normalizeSearchTermKey,
  normalizeSearchTermText,
  optionToSearchTermRef,
  refToSearchTermOption,
  resolveActiveSearchTermDuplicate,
  resolveSearchTermExcludeRows,
  type SearchTermExcludeDisplayRow,
  type SearchTermOption,
  type SearchTermRef,
} from "./monitoringSearchTermUtils";

type MonitoringType = "PRESS_RELEASE" | "KEYWORD";
type NotificationAudience = "ME" | "ALL" | "CUSTOM";

interface NotificationRecipientUser {
  sk: string;
  name: string;
  company: string;
  dept: string;
}

interface MonitoringDraft {
  title: string;
  description: string;
  type: MonitoringType;
  searchTerms: string[];
  searchTermRefs: SearchTermRef[];
  sentimentEnabled: boolean;
  sentimentKeywordMode: SentimentKeywordMode;
  negativeSentimentKeywords: string[];
  notificationEnabled: boolean;
  notifyOnNegative: boolean;
  intervalMinutes: number;
  retentionDays: number;
  activeUntilDate: string;
  notificationAudience: NotificationAudience;
  notificationRecipientUsers: NotificationRecipientUser[];
}

interface MonitoringEditState {
  mode?: "edit";
  monitoringId?: string;
  monitoring?: any;
}

interface MonitoringCreatePageProps {
  userState: userState;
}

const monitoringApi = new HttpMonitoringApi();
const getNotificationAudience = (item: any): NotificationAudience => {
  const scope = item.notificationRecipientScope;
  const recipients = item.notificationRecipients || item.recipients || [];
  if (scope === "CUSTOM" || recipients.includes("CUSTOM")) return "CUSTOM";
  if (scope === "ALL" || recipients.includes("ALL")) return "ALL";
  return "ME";
};

const mapRecipientUsers = (item: any): NotificationRecipientUser[] => {
  const snapshots = Array.isArray(item.notificationRecipientUsers) ? item.notificationRecipientUsers : [];
  if (snapshots.length) {
    return snapshots
      .map((user: any) => ({
        sk: String(user.sk || user.id || user.userId || "").trim(),
        name: String(user.name || "").trim(),
        company: String(user.company || "").trim(),
        dept: String(user.dept || "").trim(),
      }))
      .filter((user: NotificationRecipientUser) => Boolean(user.sk));
  }
  const ids = Array.isArray(item.notificationRecipientUserIds) ? item.notificationRecipientUserIds : [];
  return ids.map((id: string) => ({ sk: id, name: "", company: "", dept: "" })).filter((user: NotificationRecipientUser) => Boolean(user.sk));
};

const initialDraft: MonitoringDraft = {
  title: "",
  description: "",
  type: "KEYWORD",
  searchTerms: [],
  searchTermRefs: [],
  sentimentEnabled: false,
  sentimentKeywordMode: "GLOBAL",
  negativeSentimentKeywords: [],
  notificationEnabled: false,
  notifyOnNegative: false,
  intervalMinutes: 15,
  retentionDays: 3,
  activeUntilDate: "",
  notificationAudience: "ME",
  notificationRecipientUsers: [],
};

const sanitizeDraft = (draft: MonitoringDraft): MonitoringDraft => {
  const nextDraft = { ...draft };
  if (!nextDraft.notificationEnabled) {
    nextDraft.notifyOnNegative = false;
    nextDraft.notificationRecipientUsers = [];
  }
  if (nextDraft.notificationAudience !== "CUSTOM") {
    nextDraft.notificationRecipientUsers = [];
  }
  if (!nextDraft.sentimentEnabled) {
    nextDraft.notifyOnNegative = false;
  }
  return nextDraft;
};

const isSearchTermOptionEqual = (option: SearchTermOption, value: SearchTermOption) =>
  Boolean(option.searchTermId && value.searchTermId && option.searchTermId === value.searchTermId) ||
  option.normalizedTerm === value.normalizedTerm;

const getSearchTermRefKey = (ref: SearchTermRef) =>
  ref.searchTermId ? `id:${ref.searchTermId}` : `term:${normalizeSearchTermKey(ref.term)}`;

interface EditingSentimentKeyword {
  index: number;
}

interface SearchTermAddModalOpenOptions {
  source?: "keyboard" | "manual";
}

const MonitoringCreatePage = (props: MonitoringCreatePageProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = (location.state ?? {}) as MonitoringEditState;
  const isEditMode = routeState.mode === "edit" && Boolean(routeState.monitoringId);
  const [draft, setDraft] = useState<MonitoringDraft>(initialDraft);
  const [activeStep, setActiveStep] = useState(0);
  const [created, setCreated] = useState(false);
  const [searchTermOptions, setSearchTermOptions] = useState<SearchTermOption[]>([]);
  const [searchTermInput, setSearchTermInput] = useState("");
  const [searchTermMenuOpen, setSearchTermMenuOpen] = useState(false);
  const [searchTermAddModalOpen, setSearchTermAddModalOpen] = useState(false);
  const [searchTermExcludeInput, setSearchTermExcludeInput] = useState("");
  const [searchTermExcludeTerms, setSearchTermExcludeTerms] = useState<string[]>([]);
  const [searchTermAddEnabled, setSearchTermAddEnabled] = useState(true);
  const [searchTermAddAutoFocus, setSearchTermAddAutoFocus] = useState(true);
  const [addingSearchTerm, setAddingSearchTerm] = useState(false);
  const suppressSearchTermAddSubmitRef = useRef(false);
  const [negativeKeywords, setNegativeKeywords] = useState<string[]>([]);
  const [negativeKeywordInput, setNegativeKeywordInput] = useState("");
  const [editingSentimentKeyword, setEditingSentimentKeyword] = useState<EditingSentimentKeyword | null>(null);
  const [recipientUserOptions, setRecipientUserOptions] = useState<NotificationRecipientUser[]>([]);
  const [recipientUserInput, setRecipientUserInput] = useState("");
  const [recipientUserMenuOpen, setRecipientUserMenuOpen] = useState(false);
  const [loadingRecipientUsers, setLoadingRecipientUsers] = useState(false);
  const [pressReleaseMonitoringAvailable, setPressReleaseMonitoringAvailable] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [editingOriginalType, setEditingOriginalType] = useState<MonitoringType | null>(null);
  const trimmedSearchTermInput = normalizeSearchTermText(searchTermInput);
  const normalizedSearchTermInput = normalizeSearchTermKey(searchTermInput);
  const selectedSearchTermExcludeRows = resolveSearchTermExcludeRows(draft.searchTermRefs, searchTermOptions);
  const filteredSearchTermOptions = normalizedSearchTermInput
    ? searchTermOptions.filter((option) => option.normalizedTerm.includes(normalizedSearchTermInput))
    : searchTermOptions;
  const isSelectedSearchTermOption = (option: SearchTermOption) =>
    draft.searchTermRefs.some((ref) => isSearchTermOptionEqual(refToSearchTermOption(ref), option));
  const selectedFilteredSearchTermCount = filteredSearchTermOptions.filter(isSelectedSearchTermOption).length;
  const allFilteredSearchTermsSelected =
    filteredSearchTermOptions.length > 0 && selectedFilteredSearchTermCount === filteredSearchTermOptions.length;
  const activeDuplicateSearchTerm = resolveActiveSearchTermDuplicate(searchTermOptions, searchTermInput);
  const effectiveNegativeKeywords =
    draft.sentimentKeywordMode === "CUSTOM" ? draft.negativeSentimentKeywords : negativeKeywords;
  const normalizedRecipientUserInput = recipientUserInput.trim().toLowerCase();
  const visibleRecipientUserOptions = normalizedRecipientUserInput
    ? recipientUserOptions.filter((option) => getRecipientUserSearchText(option).includes(normalizedRecipientUserInput))
    : recipientUserOptions;
  const isSelectedRecipientUser = (user: NotificationRecipientUser) =>
    draft.notificationRecipientUsers.some((item) => item.sk === user.sk);

  useEffect(() => {
    if (!draft.notificationEnabled || draft.notificationAudience !== "CUSTOM") {
      setRecipientUserMenuOpen(false);
      return;
    }
    let ignore = false;
    const loadRecipientUsers = async () => {
      setLoadingRecipientUsers(true);
      try {
        const response = await monitoringApi.post({
          command: "list_notification_recipient_users",
          query: recipientUserInput,
          limit: 100,
        });
        if (ignore) return;
        if (response?.code === "200") {
          setRecipientUserOptions((response.response?.users ?? []) as NotificationRecipientUser[]);
        }
      } catch {
        if (!ignore) setRecipientUserOptions([]);
      } finally {
        if (!ignore) setLoadingRecipientUsers(false);
      }
    };
    const timer = window.setTimeout(loadRecipientUsers, 180);
    return () => {
      ignore = true;
      window.clearTimeout(timer);
    };
  }, [draft.notificationAudience, draft.notificationEnabled, recipientUserInput]);

  useEffect(() => {
    let ignore = false;
    // 새 모니터링 생성 화면에서 선택할 검색어와 부정 판정 키워드 옵션을 API에서 불러온다.
    const loadOptions = async () => {
      setLoadingOptions(true);
      setStatusMessage(null);
      try {
        const response = await monitoringApi.post({ command: "get_monitoring_config_options" });
        if (ignore) return;
        if (response?.code === "200") {
          const nextSearchTermOptions = ((response.response?.searchTerms ?? []) as any[])
            .map((item) => mapSearchTermOption(item, "CONFIG"))
            .filter((item): item is SearchTermOption => Boolean(item?.enabled));
          const nextNegativeKeywords = ((response.response?.negativeKeywords ?? []) as any[])
            .filter((item) => item.enabled !== false)
            .map((item) => item.value || item.keyword)
            .filter(Boolean);
          setSearchTermOptions((prev) => mergeSearchTermOptions(nextSearchTermOptions, prev.filter((option) => option.source === "INLINE_CREATED")));
          setNegativeKeywords(nextNegativeKeywords);
          setPressReleaseMonitoringAvailable(response.response?.pressReleaseMonitoringAvailable === true);
          setDraft((prev) => {
            const nextRefs = mergeSearchTermRefs(
              prev.searchTermRefs.filter((ref) => {
                const refOption = refToSearchTermOption(ref);
                return ref.source === "INLINE_CREATED" || nextSearchTermOptions.some((option) => isSearchTermOptionEqual(option, refOption));
              })
            );
            return { ...prev, searchTermRefs: nextRefs, searchTerms: getSearchTermNames(nextRefs) };
          });
        } else {
          setStatusMessage(response?.response?.error_msg ?? "모니터링 옵션을 불러오지 못했습니다.");
        }
      } catch (error) {
        if (!ignore) setStatusMessage(error instanceof Error ? error.message : "모니터링 옵션을 불러오지 못했습니다.");
      } finally {
        if (!ignore) setLoadingOptions(false);
      }
    };
    loadOptions();
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (!isEditMode) return;
    let ignore = false;
    const applyMonitoring = (item: any) => {
      const nextType = item.monitoringType === "PRESS_RELEASE" ? "PRESS_RELEASE" : "KEYWORD";
      const notificationEnabled = item.notificationEnabled !== false && item.notificationPolicy !== "NONE";
      const sentimentEnabled = item.sentimentEnabled !== false;
      const nextSearchTermRefs = buildSearchTermRefsFromMonitoring(item);
      const nextSentimentConfig = buildSentimentKeywordConfigFromMonitoring(item);
      setEditingOriginalType(nextType);
      setSearchTermOptions((prev) => mergeSearchTermOptions(prev, nextSearchTermRefs.map(refToSearchTermOption)));
      setNegativeKeywordInput("");
      setEditingSentimentKeyword(null);
      setDraft(sanitizeDraft({
        title: item.title || item.name || "",
        description: item.description || "",
        type: nextType,
        searchTerms: getSearchTermNames(nextSearchTermRefs),
        searchTermRefs: nextSearchTermRefs,
        sentimentEnabled,
        sentimentKeywordMode: nextSentimentConfig.sentimentKeywordMode,
        negativeSentimentKeywords: nextSentimentConfig.negativeSentimentKeywords,
        notificationEnabled,
        notifyOnNegative:
          item.notifyOnNegative !== undefined
            ? item.notifyOnNegative === true
            : notificationEnabled && sentimentEnabled,
        intervalMinutes: Number(item.runIntervalMinutes ?? item.intervalMinutes ?? 15),
        retentionDays: Math.min(7, Math.max(1, Number(item.articleRetentionDays ?? item.retentionDays ?? 3))),
        activeUntilDate: item.activeUntilDate || (item.activeUntil ? String(item.activeUntil).slice(0, 10) : ""),
        notificationAudience: getNotificationAudience(item),
        notificationRecipientUsers: mapRecipientUsers(item),
      }));
    };

    if (routeState.monitoring) {
      applyMonitoring(routeState.monitoring);
    }

    const loadMonitoring = async () => {
      try {
        const response = await monitoringApi.post({ command: "get_monitoring", monitoringId: routeState.monitoringId });
        if (ignore) return;
        if (response?.code === "200" && response.response?.monitoring) {
          applyMonitoring(response.response.monitoring);
        } else if (!routeState.monitoring) {
          setStatusMessage(response?.response?.error_msg ?? "모니터링 설정을 불러오지 못했습니다.");
        }
      } catch (error) {
        if (!ignore && !routeState.monitoring) {
          setStatusMessage(error instanceof Error ? error.message : "모니터링 설정을 불러오지 못했습니다.");
        }
      }
    };

    loadMonitoring();
    return () => {
      ignore = true;
    };
  }, [isEditMode, routeState.monitoring, routeState.monitoringId]);

  const searchTermsSatisfied = draft.type !== "KEYWORD" || draft.searchTermRefs.length > 0;
  const pressReleaseSelectable = pressReleaseMonitoringAvailable || editingOriginalType === "PRESS_RELEASE";
  const monitoringTypeSatisfied = draft.type !== "PRESS_RELEASE" || pressReleaseSelectable;
  const canCreate = Boolean(
    draft.title.trim() &&
      searchTermsSatisfied &&
      monitoringTypeSatisfied &&
      !addingSearchTerm &&
      draft.intervalMinutes > 0 &&
      draft.retentionDays > 0 &&
      draft.retentionDays <= 7 &&
      (!draft.notificationEnabled || (draft.notificationAudience && (draft.notificationAudience !== "CUSTOM" || draft.notificationRecipientUsers.length > 0)))
  );

  // 공통 draft 변경 함수로, 사용자가 값을 바꾸면 생성 완료 상태를 초기화한다.
  const updateDraft = <K extends keyof MonitoringDraft,>(key: K, value: MonitoringDraft[K]) => {
    setDraft((prev) => sanitizeDraft({ ...prev, [key]: value }));
    setCreated(false);
  };

  const applySearchTermRefs = (refs: SearchTermRef[]) => {
    const nextRefs = mergeSearchTermRefs(refs);
    setDraft((prev) => sanitizeDraft({ ...prev, searchTermRefs: nextRefs, searchTerms: getSearchTermNames(nextRefs) }));
    setCreated(false);
  };

  const appendSearchTermRef = (ref: SearchTermRef) => {
    setDraft((prev) => {
      const nextRefs = mergeSearchTermRefs([...prev.searchTermRefs, ref]);
      return sanitizeDraft({ ...prev, searchTermRefs: nextRefs, searchTerms: getSearchTermNames(nextRefs) });
    });
    setCreated(false);
  };

  // 검색어 칩을 선택/해제하여 모니터링에 포함할 검색어 목록을 갱신한다.
  const toggleSearchTerm = (ref: SearchTermRef) => {
    const refOption = refToSearchTermOption(ref);
    const exists = draft.searchTermRefs.some((item) => isSearchTermOptionEqual(refToSearchTermOption(item), refOption));
    applySearchTermRefs(
      exists
        ? draft.searchTermRefs.filter((item) => !isSearchTermOptionEqual(refToSearchTermOption(item), refOption))
        : [...draft.searchTermRefs, ref]
    );
  };

  const selectFilteredSearchTerms = () => {
    if (filteredSearchTermOptions.length === 0) return;
    applySearchTermRefs([...draft.searchTermRefs, ...filteredSearchTermOptions.map(optionToSearchTermRef)]);
  };

  const toggleFilteredSearchTerms = () => {
    if (filteredSearchTermOptions.length === 0) return;
    if (!allFilteredSearchTermsSelected) {
      selectFilteredSearchTerms();
      return;
    }
    applySearchTermRefs(
      draft.searchTermRefs.filter(
        (ref) =>
          !filteredSearchTermOptions.some((option) => isSearchTermOptionEqual(refToSearchTermOption(ref), option))
      )
    );
  };

  const clearSelectedSearchTerms = () => {
    applySearchTermRefs([]);
  };

  const toggleRecipientUser = (user: NotificationRecipientUser) => {
    setDraft((prev) => {
      const exists = prev.notificationRecipientUsers.some((item) => item.sk === user.sk);
      return sanitizeDraft({
        ...prev,
        notificationRecipientUsers: exists
          ? prev.notificationRecipientUsers.filter((item) => item.sk !== user.sk)
          : [...prev.notificationRecipientUsers, user],
      });
    });
    setCreated(false);
  };

  const clearRecipientUsers = () => {
    updateDraft("notificationRecipientUsers", []);
  };

  const resetSearchTermAddOptions = () => {
    setSearchTermExcludeInput("");
    setSearchTermExcludeTerms([]);
    setSearchTermAddEnabled(true);
    setSearchTermAddAutoFocus(true);
    suppressSearchTermAddSubmitRef.current = false;
  };

  const openSearchTermAddModal = (options?: SearchTermAddModalOpenOptions) => {
    const openedByKeyboard = options?.source === "keyboard";
    setSearchTermMenuOpen(false);
    setRecipientUserMenuOpen(false);
    resetSearchTermAddOptions();
    setSearchTermAddAutoFocus(!openedByKeyboard);
    suppressSearchTermAddSubmitRef.current = openedByKeyboard;
    setSearchTermAddModalOpen(true);
  };

  const closeSearchTermAddModal = () => {
    if (addingSearchTerm) return;
    resetSearchTermAddOptions();
    setSearchTermAddModalOpen(false);
  };

  const addSearchTermExcludeInput = () => {
    const nextTerms = normalizeExcludeTerms(searchTermExcludeInput);
    if (!nextTerms.length) return;
    setSearchTermExcludeTerms((prev) => normalizeExcludeTerms([...prev, ...nextTerms]));
    setSearchTermExcludeInput("");
  };

  const updateSearchTermExcludeInput = (value: string) => {
    if (/[,\n]/.test(value)) {
      const nextTerms = normalizeExcludeTerms(value);
      if (nextTerms.length) {
        setSearchTermExcludeTerms((prev) => normalizeExcludeTerms([...prev, ...nextTerms]));
        setSearchTermExcludeInput("");
        return;
      }
    }
    setSearchTermExcludeInput(value);
  };

  const deleteSearchTermExcludeTerm = (term: string) => {
    setSearchTermExcludeTerms((prev) => prev.filter((item) => item !== term));
  };

  const releaseSearchTermAddSubmitLock = () => {
    suppressSearchTermAddSubmitRef.current = false;
  };

  const updateSentimentKeywordMode = (mode: SentimentKeywordMode) => {
    setDraft((prev) => {
      return sanitizeDraft({
        ...prev,
        sentimentKeywordMode: mode,
        negativeSentimentKeywords: mode === "CUSTOM" ? prev.negativeSentimentKeywords : [],
      });
    });
    setCreated(false);
    setEditingSentimentKeyword(null);
    setNegativeKeywordInput("");
  };

  const resetSentimentKeywordsToGlobal = () => {
    setDraft((prev) => sanitizeDraft({
      ...prev,
      sentimentKeywordMode: "GLOBAL",
      negativeSentimentKeywords: [],
    }));
    setCreated(false);
    setEditingSentimentKeyword(null);
    setNegativeKeywordInput("");
  };

  // 맞춤 키워드 모드를 유지한 채 전역 부정 키워드를 맞춤 목록에 한꺼번에 병합한다.
  const addGlobalKeywordsToCustom = () => {
    setDraft((prev) => sanitizeDraft({
      ...prev,
      sentimentKeywordMode: "CUSTOM",
      negativeSentimentKeywords: mergeSentimentKeywordLists(prev.negativeSentimentKeywords, negativeKeywords),
    }));
    setCreated(false);
  };

  const updateNegativeSentimentKeywords = (keywords: string[]) => {
    setDraft((prev) => sanitizeDraft({
      ...prev,
      sentimentKeywordMode: "CUSTOM",
      negativeSentimentKeywords: keywords,
    }));
    setCreated(false);
  };

  const submitSentimentKeyword = () => {
    const value = normalizeSentimentKeywordText(negativeKeywordInput);
    if (!value) return;
    const editingIndex = editingSentimentKeyword?.index;
    updateNegativeSentimentKeywords(upsertSentimentKeyword(draft.negativeSentimentKeywords, value, editingIndex));
    setNegativeKeywordInput("");
    setEditingSentimentKeyword(null);
  };

  const editSentimentKeyword = (index: number, keyword: string) => {
    setNegativeKeywordInput(keyword);
    setEditingSentimentKeyword({ index });
  };

  const deleteSentimentKeyword = (index: number) => {
    updateNegativeSentimentKeywords(removeSentimentKeyword(draft.negativeSentimentKeywords, index));
    if (editingSentimentKeyword?.index === index) {
      setNegativeKeywordInput("");
      setEditingSentimentKeyword(null);
    }
  };

  const handleAddSearchTerm = async (): Promise<boolean> => {
    if (!trimmedSearchTermInput || addingSearchTerm) return false;
    if (activeDuplicateSearchTerm) {
      appendSearchTermRef(optionToSearchTermRef(activeDuplicateSearchTerm));
      setSearchTermInput("");
      setStatusMessage(null);
      return true;
    }

    setAddingSearchTerm(true);
    setStatusMessage(null);
    const excludeTerms = normalizeExcludeTerms(searchTermExcludeTerms);
    try {
      const response = await monitoringApi.post({
        command: "upsert_search_term",
        term: trimmedSearchTermInput,
        value: trimmedSearchTermInput,
        enabled: searchTermAddEnabled,
        excludeTerms,
      });
      if (response?.code === "200") {
        const responseItem = response.response?.searchTerm ?? response.response?.item ?? response.response?.keyword ?? {
          term: trimmedSearchTermInput,
          source: "INLINE_CREATED",
          enabled: searchTermAddEnabled,
          excludeTerms,
        };
        const mappedOption = mapSearchTermOption(responseItem, "INLINE_CREATED");
        const nextOption: SearchTermOption =
          mappedOption
            ? {
                ...mappedOption,
                enabled: searchTermAddEnabled,
                excludeTerms: mappedOption.excludeTerms.length ? mappedOption.excludeTerms : excludeTerms,
              }
            : {
                term: trimmedSearchTermInput,
                normalizedTerm: normalizeSearchTermKey(trimmedSearchTermInput),
                enabled: searchTermAddEnabled,
                source: "INLINE_CREATED",
                excludeTerms,
              };
        setSearchTermOptions((prev) => mergeSearchTermOptions(prev, [nextOption]));
        appendSearchTermRef(optionToSearchTermRef(nextOption));
        setSearchTermInput("");
        return true;
      } else {
        setStatusMessage(response?.response?.error_msg ?? "검색어 추가에 실패했습니다.");
      }
    } catch (error) {
      setStatusMessage(error instanceof Error ? error.message : "검색어 추가에 실패했습니다.");
    } finally {
      setAddingSearchTerm(false);
    }
    return false;
  };

  const submitSearchTermAddModal = async () => {
    if (suppressSearchTermAddSubmitRef.current) return;
    const added = await handleAddSearchTerm();
    if (added) {
      resetSearchTermAddOptions();
      setSearchTermAddModalOpen(false);
    }
  };

  // 화면 입력값을 create/update command로 변환해 모니터링 설정을 저장한다.
  const createMonitoring = async () => {
    if (!canCreate || saving || addingSearchTerm) return;
    setSaving(true);
    setCreated(false);
    setStatusMessage(null);
    const selectedSearchTermRefs = mergeSearchTermRefs(draft.searchTermRefs);
    const selectedSearchTerms = getSearchTermNames(selectedSearchTermRefs);
    try {
      const response = await monitoringApi.post({
        command: isEditMode ? "update_monitoring" : "create_monitoring",
        userId: props.userState.id,
        monitoringId: isEditMode ? routeState.monitoringId : undefined,
        title: draft.title,
        description: draft.description,
        monitoringType: draft.type,
        searchTerms: selectedSearchTerms,
        searchTermIds: getSearchTermIds(selectedSearchTermRefs),
        searchTermRefs: selectedSearchTermRefs,
        sentimentEnabled: draft.sentimentEnabled,
        sentimentKeywordMode: draft.sentimentKeywordMode,
        positiveSentimentKeywords: draft.sentimentKeywordMode === "CUSTOM" ? [] : undefined,
        negativeSentimentKeywords: draft.sentimentKeywordMode === "CUSTOM" ? draft.negativeSentimentKeywords : undefined,
        notificationEnabled: draft.notificationEnabled,
        notificationPolicy: draft.notificationEnabled ? "BATCHED" : "NONE",
        notificationRecipients: draft.notificationEnabled ? [draft.notificationAudience] : [],
        notificationRecipientScope: draft.notificationEnabled ? draft.notificationAudience : "ME",
        notificationRecipientUserIds: draft.notificationEnabled && draft.notificationAudience === "CUSTOM" ? draft.notificationRecipientUsers.map((user) => user.sk) : [],
        notificationRecipientUsers: draft.notificationEnabled && draft.notificationAudience === "CUSTOM" ? draft.notificationRecipientUsers : [],
        notifyOnNegative: draft.notifyOnNegative,
        runIntervalMinutes: draft.intervalMinutes,
        articleRetentionDays: draft.retentionDays,
        activeUntilDate: draft.activeUntilDate,
      });
      if (response?.code === "200") {
        const savedMonitoring = response.response?.monitoring;
        const savedMonitoringId = savedMonitoring?.monitoringId || savedMonitoring?.id || routeState.monitoringId;
        navigate(buildMonitoringDetailUrl({ monitoringId: savedMonitoringId, tab: "settings" }));
      } else {
        setStatusMessage(response?.response?.error_msg ?? (isEditMode ? "모니터링 수정에 실패했습니다." : "모니터링 생성에 실패했습니다."));
      }
    } catch (error) {
      setStatusMessage(error instanceof Error ? error.message : isEditMode ? "모니터링 수정에 실패했습니다." : "모니터링 생성에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={pageSx}>
      <Stack spacing={4} sx={contentShellSx}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.5}>
          <Box>
            <Typography sx={pageTitleSx}>{isEditMode ? "모니터링 설정 수정" : "새 모니터링 만들기"}</Typography>
            <Typography sx={pageSubtitleSx}>
              {loadingOptions ? "모니터링 옵션을 불러오는 중입니다." : statusMessage ?? (isEditMode ? "생성 화면과 같은 항목으로 모니터링 설정을 수정합니다." : "질문에 답하듯 설정하면 기사 수집, 긍부정 판단, 알림 정책이 함께 만들어집니다.")}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/pressRelease")} sx={headerActionButtonSx}>
              모니터링 목록 보기
            </Button>
            <Button startIcon={<ImageIcon src={keywordAddIconSrc} alt="" />} aria-label="키워드 추가 모달 열기" onClick={() => openSearchTermAddModal()} sx={headerActionButtonSx}>
              키워드 추가
            </Button>
          </Stack>
        </Stack>

        <Box sx={createLayoutSx}>
          <Paper variant="outlined" sx={{ ...panelSx, position: { lg: "sticky" }, top: 16 }}>
            <PanelHeader icon={<ImageIcon src={settingsStepsIconSrc} alt="" size={16} />} title="설정 단계" />
            <Stack spacing={0} sx={{ p: 3, pt: 2 }}>
              {steps
                .map((step, index) => ({ ...step, stepIndex: index }))
                .filter((step) => draft.type === "KEYWORD" || step.stepIndex !== 1)
                .map((step, displayIndex, visibleSteps) => (
                  <Box key={step.title}>
                    <StepButton
                      active={activeStep === step.stepIndex}
                      complete={getStepComplete(step.stepIndex, draft, pressReleaseSelectable)}
                      index={displayIndex + 1}
                      title={step.title}
                      desc={step.desc}
                      onClick={() => setActiveStep(step.stepIndex)}
                    />
                    {displayIndex < visibleSteps.length - 1 && (
                      <Box sx={stepConnectorSx}>
                        <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                      </Box>
                    )}
                  </Box>
                ))}
            </Stack>
          </Paper>

          <Stack spacing={2}>
            <Paper variant="outlined" sx={sectionSx(activeStep === 0)}>
              <SectionHeader index={1} title="어떤 방식으로 모니터링할까요?" />
              <Stack spacing={4} sx={{ p: 3 }}>
                <Box>
                  <Typography sx={fieldLabelSx}>모니터링 이름</Typography>
                  <TextField
                    size="small"
                    value={draft.title}
                    onChange={(event) => updateDraft("title", event.target.value)}
                    placeholder="모니터링 이름을 입력해주세요"
                    inputProps={{ "aria-label": "모니터링 이름" }}
                    sx={textFieldSx}
                    fullWidth
                  />
                </Box>
                <Box>
                  <Typography sx={fieldLabelSx}>모니터링 설명</Typography>
                  <TextField
                    multiline
                    minRows={3}
                    size="small"
                    value={draft.description}
                    onChange={(event) => updateDraft("description", event.target.value)}
                    placeholder="모니터링 설명을 입력해주세요"
                    inputProps={{ "aria-label": "모니터링 설명" }}
                    sx={textFieldSx}
                    fullWidth
                  />
                </Box>
                <Box>
                  <Typography sx={fieldLabelSx}>모니터링 기반</Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                    <ChoiceCard
                      selected={draft.type === "PRESS_RELEASE"}
                      disabled={!pressReleaseSelectable}
                      title="보도자료 기반"
                      badge={pressReleaseSelectable ? undefined : "이미 생성됨"}
                      desc={pressReleaseSelectable ? "배포한 보도자료와 연관 기사를 하나의 전용 모니터링으로 관리합니다." : "보도자료 기반 모니터링은 시스템에서 하나만 운영할 수 있습니다."}
                      onClick={() => {
                        updateDraft("type", "PRESS_RELEASE");
                        setActiveStep(0);
                      }}
                    />
                    <ChoiceCard
                      selected={draft.type === "KEYWORD"}
                      title="키워드 기반"
                      desc="검색어를 직접 선택해 관심 기사와 리스크 기사를 주기적으로 수집합니다."
                      onClick={() => updateDraft("type", "KEYWORD")}
                    />
                  </Box>
                </Box>
              </Stack>
            </Paper>

            {draft.type === "KEYWORD" && (
              <Paper variant="outlined" sx={keywordSectionSx(activeStep === 1, searchTermMenuOpen)}>
                <SectionHeader index={2} title="어떤 기사들을 찾을까요?" />
                <Stack spacing={4} sx={{ p: 3 }}>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={keywordLabelRowSx}>
                      <Typography sx={{ ...fieldLabelSx, mb: 0 }}>검색 키워드 선택</Typography>
                    </Stack>
                    <ClickAwayListener onClickAway={() => setSearchTermMenuOpen(false)}>
                      <Box sx={keywordPickerSx}>
                        <TextField
                          value={searchTermInput}
                          placeholder="검색 키워드를 입력해 후보를 좁히세요"
                          size="small"
                          fullWidth
                          onFocus={() => setSearchTermMenuOpen(true)}
                          onClick={() => setSearchTermMenuOpen(true)}
                          onChange={(event) => setSearchTermInput(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              if (event.nativeEvent.isComposing) return;
                              event.preventDefault();
                              event.stopPropagation();
                              if (activeDuplicateSearchTerm) {
                                void handleAddSearchTerm();
                              } else {
                                openSearchTermAddModal({ source: "keyboard" });
                              }
                            }
                          }}
                          InputProps={{
                            endAdornment: (
                              <Box
                                component="img"
                                src={monitoringSelectArrowIconSrc}
                                alt=""
                                aria-hidden="true"
                                draggable={false}
                                sx={keywordSelectArrowSx(searchTermMenuOpen)}
                              />
                            ),
                          }}
                          inputProps={{ "aria-label": "검색 키워드 선택" }}
                          sx={keywordInputSx}
                        />
                        {searchTermMenuOpen && (
                          <Paper variant="outlined" sx={keywordDropdownSx(filteredSearchTermOptions.length)}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={keywordDropdownHeaderSx}>
                              <Typography sx={keywordDropdownCountSx}>
                                {searchTermInput ? `검색 결과 ${filteredSearchTermOptions.length}개` : `전체 ${searchTermOptions.length}개`}
                              </Typography>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Checkbox
                                  size="small"
                                  checked={allFilteredSearchTermsSelected}
                                  indeterminate={!allFilteredSearchTermsSelected && selectedFilteredSearchTermCount > 0}
                                  onChange={toggleFilteredSearchTerms}
                                  icon={<EmptyCheckboxIcon />}
                                  checkedIcon={<CheckedCheckboxIcon />}
                                  indeterminateIcon={<IndeterminateCheckboxIcon />}
                                  sx={figmaCheckboxSx}
                                />
                                <Typography sx={keywordSelectAllTextSx}>전체 선택</Typography>
                              </Stack>
                            </Stack>
                            <Stack sx={keywordOptionListSx}>
                              {filteredSearchTermOptions.length ? (
                                filteredSearchTermOptions.map((option) => {
                                  const selected = isSelectedSearchTermOption(option);
                                  return (
                                    <Box key={option.searchTermId ?? option.normalizedTerm} sx={keywordOptionRowSx} onClick={() => toggleSearchTerm(optionToSearchTermRef(option))}>
                                      <Checkbox
                                        checked={selected}
                                        size="small"
                                        icon={<EmptyCheckboxIcon />}
                                        checkedIcon={<CheckedCheckboxIcon />}
                                        sx={figmaCheckboxSx}
                                        inputProps={{ "aria-label": `${option.term} 선택` }}
                                        onClick={(event) => event.stopPropagation()}
                                        onChange={() => toggleSearchTerm(optionToSearchTermRef(option))}
                                      />
                                      <Typography sx={autocompleteOptionTextSx}>{option.term}</Typography>
                                    </Box>
                                  );
                                })
                              ) : (
                                <Typography sx={keywordEmptySx}>검색 결과가 없습니다.</Typography>
                              )}
                            </Stack>
                          </Paper>
                        )}
                      </Box>
                    </ClickAwayListener>
                  </Box>

                  <Stack sx={searchConditionGroupSx}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                      <Typography sx={{ color: "#4a5568", fontSize: 12, fontWeight: 700 }}>
                        선택된 검색어 <Box component="span" sx={{ color: "#4f74f9" }}>{draft.searchTermRefs.length}개</Box>
                      </Typography>
                      <Button
                        size="small"
                        sx={dangerTextButtonSx}
                        disabled={draft.searchTermRefs.length === 0}
                        onClick={clearSelectedSearchTerms}
                      >
                        전체 삭제
                      </Button>
                    </Stack>
                    <Box sx={selectedKeywordWellSx}>
                      <Stack direction="row" sx={selectedSearchTermListSx}>
                        {draft.searchTermRefs.length ? (
                          draft.searchTermRefs.map((ref) => (
                            <Chip
                              key={getSearchTermRefKey(ref)}
                              label={ref.term}
                              size="small"
                              onDelete={() => toggleSearchTerm(ref)}
                              deleteIcon={<ChipDeleteIcon />}
                              sx={selectedSearchTermChipSx}
                            />
                          ))
                        ) : (
                          <Typography sx={selectedSearchTermEmptySx}>선택된 검색어가 없습니다</Typography>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </Stack>
              </Paper>
            )}

            <Paper variant="outlined" sx={processingSectionSx(activeStep === 2, recipientUserMenuOpen)}>
              <SectionHeader index={draft.type === "KEYWORD" ? 3 : 2} title="발견된 기사를 어떻게 처리할까요?" />
              <Stack spacing={4} sx={{ p: 3 }}>
                <Box sx={configSectionSx}>
                  <Typography sx={configSectionTitleSx}>기능 설정</Typography>
                  <Stack spacing={1}>
                    <SettingSwitch
                      title="긍정/부정으로 판단하기"
                      desc="전역으로 관리되는 긍정·부정 키워드를 적용합니다."
                      checked={draft.sentimentEnabled}
                      onChange={(checked) => updateDraft("sentimentEnabled", checked)}
                    />
                    {draft.sentimentEnabled && (
                      <Box sx={sentimentKeywordBoxSx}>
                        <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "flex-start" }} justifyContent="space-between" spacing={1} sx={sentimentKeywordScopeHeaderSx}>
                          <Box>
                            <Typography sx={notificationPanelLabelSx}>판정 키워드 범위</Typography>
                            <Typography sx={sentimentKeywordScopeDescSx}>
                              {draft.sentimentKeywordMode === "CUSTOM" ? "이 모니터링 저장 payload에만 맞춤 키워드를 포함합니다." : "전역 키워드 설정을 그대로 사용합니다."}
                            </Typography>
                          </Box>
                          <Chip
                            label={draft.sentimentKeywordMode === "CUSTOM" ? "맞춤 적용" : "전역 적용"}
                            size="small"
                            sx={draft.sentimentKeywordMode === "CUSTOM" ? selectedTinyChipSx : smallChipSx}
                          />
                        </Stack>
                        <RadioGroup
                          row
                          value={draft.sentimentKeywordMode}
                          onChange={(event) => updateSentimentKeywordMode(event.target.value as SentimentKeywordMode)}
                          sx={sentimentModeRadioGroupSx}
                        >
                          <FormControlLabel value="GLOBAL" control={<Radio size="small" icon={<EmptyRadioIcon />} checkedIcon={<CheckedRadioIcon />} sx={figmaRadioSx} />} label="전역 키워드" sx={notificationRadioLabelSx} />
                          <FormControlLabel value="CUSTOM" control={<Radio size="small" icon={<EmptyRadioIcon />} checkedIcon={<CheckedRadioIcon />} sx={figmaRadioSx} />} label="맞춤 키워드" sx={notificationRadioLabelSx} />
                        </RadioGroup>
                        {draft.sentimentKeywordMode === "GLOBAL" ? (
                          <Box sx={keywordPreviewSx}>
                            <KeywordGroup label="부정 키워드" keywords={negativeKeywords} empty="설정된 전역 부정 키워드가 없습니다." />
                          </Box>
                        ) : (
                          <Stack spacing={1}>
                            <Box sx={customKeywordGridSx}>
                              <SentimentKeywordEditor
                                label="부정 키워드"
                                keywords={draft.negativeSentimentKeywords}
                                inputValue={negativeKeywordInput}
                                editing={Boolean(editingSentimentKeyword)}
                                onInputChange={setNegativeKeywordInput}
                                onSubmit={submitSentimentKeyword}
                                onEdit={editSentimentKeyword}
                                onDelete={deleteSentimentKeyword}
                              />
                            </Box>
                            <Stack direction="row" justifyContent="flex-end" spacing={1}>
                              <Button
                                size="small"
                                variant="outlined"
                                sx={miniOutlineButtonSx}
                                onClick={addGlobalKeywordsToCustom}
                                disabled={negativeKeywords.length === 0}
                              >
                                전역 부정 키워드 전체 추가
                              </Button>
                              <Button size="small" variant="outlined" sx={miniOutlineButtonSx} onClick={resetSentimentKeywordsToGlobal}>
                                전역 기본값으로 되돌리기
                              </Button>
                            </Stack>
                          </Stack>
                        )}
                      </Box>
                    )}

                  </Stack>
                </Box>

                <Box sx={configSectionSx}>
                  <Typography sx={configSectionTitleSx}>알림 설정</Typography>
                  <Stack spacing={1}>
                    <SettingSwitch
                      title="알림 보내기"
                      desc="조건에 맞는 기사나 결과가 생기면 Teams로 알립니다."
                      checked={draft.notificationEnabled}
                      onChange={(checked) => updateDraft("notificationEnabled", checked)}
                    />
                    {draft.notificationEnabled && (
                      <Box sx={notificationSettingsPanelSx}>
                        <Box sx={notificationScopeSectionSx}>
                          <Typography sx={notificationPanelLabelSx}>알림 수신 범위</Typography>
                          <RadioGroup
                            row
                            value={draft.notificationAudience}
                            onChange={(event) => {
                              const audience = event.target.value as NotificationAudience;
                              updateDraft("notificationAudience", audience);
                              if (audience !== "CUSTOM") setRecipientUserMenuOpen(false);
                            }}
                            sx={notificationRadioGroupSx}
                          >
                            <FormControlLabel value="ME" control={<Radio size="small" icon={<EmptyRadioIcon />} checkedIcon={<CheckedRadioIcon />} sx={figmaRadioSx} />} label="나에게만 전송" sx={notificationRadioLabelSx} />
                            <FormControlLabel value="ALL" control={<Radio size="small" icon={<EmptyRadioIcon />} checkedIcon={<CheckedRadioIcon />} sx={figmaRadioSx} />} label="홍보실 전체" sx={notificationRadioLabelSx} />
                            <FormControlLabel value="CUSTOM" control={<Radio size="small" icon={<EmptyRadioIcon />} checkedIcon={<CheckedRadioIcon />} sx={figmaRadioSx} />} label="사용자 지정" sx={notificationRadioLabelSx} />
                          </RadioGroup>
                          {draft.notificationAudience === "CUSTOM" && (
                            <Box sx={recipientUserSectionSx}>
                              <Typography sx={notificationPanelLabelSx}>알림 받을 사용자</Typography>
                              <ClickAwayListener onClickAway={() => setRecipientUserMenuOpen(false)}>
                                <Box sx={recipientPickerSx}>
                                  <TextField
                                    value={recipientUserInput}
                                    placeholder="사용자 ID를 선택해주세요."
                                    size="small"
                                    fullWidth
                                    onFocus={() => setRecipientUserMenuOpen(true)}
                                    onClick={() => setRecipientUserMenuOpen(true)}
                                    onChange={(event) => {
                                      setRecipientUserInput(event.target.value);
                                      setRecipientUserMenuOpen(true);
                                    }}
                                    onKeyDown={(event) => {
                                      if (event.key === "Enter") {
                                        event.preventDefault();
                                        const firstOption = visibleRecipientUserOptions[0];
                                        if (firstOption) toggleRecipientUser(firstOption);
                                      }
                                      if (event.key === "Escape") {
                                        setRecipientUserMenuOpen(false);
                                      }
                                    }}
                                    InputProps={{
                                      endAdornment: (
                                        <Box
                                          component="img"
                                          src={monitoringSelectArrowIconSrc}
                                          alt=""
                                          aria-hidden="true"
                                          draggable={false}
                                          sx={keywordSelectArrowSx(recipientUserMenuOpen)}
                                        />
                                      ),
                                    }}
                                    inputProps={{ "aria-label": "알림 받을 사용자" }}
                                    sx={recipientInputSx}
                                  />
                                  {recipientUserMenuOpen && (
                                    <Paper variant="outlined" sx={keywordDropdownSx(visibleRecipientUserOptions.length)}>
                                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={keywordDropdownHeaderSx}>
                                        <Typography sx={keywordDropdownCountSx}>
                                          {loadingRecipientUsers ? "사용자 검색 중" : recipientUserInput ? `검색 결과 ${visibleRecipientUserOptions.length}개` : `전체 ${visibleRecipientUserOptions.length}개`}
                                        </Typography>
                                      </Stack>
                                      <Stack sx={keywordOptionListSx}>
                                        {loadingRecipientUsers ? (
                                          <Typography sx={keywordEmptySx}>사용자를 불러오는 중입니다.</Typography>
                                        ) : visibleRecipientUserOptions.length ? (
                                          visibleRecipientUserOptions.map((user) => {
                                            const selected = isSelectedRecipientUser(user);
                                            return (
                                              <Box key={user.sk} sx={keywordOptionRowSx} onClick={() => toggleRecipientUser(user)}>
                                                <Checkbox
                                                  checked={selected}
                                                  size="small"
                                                  icon={<EmptyCheckboxIcon />}
                                                  checkedIcon={<CheckedCheckboxIcon />}
                                                  sx={figmaCheckboxSx}
                                                  inputProps={{ "aria-label": `${getRecipientUserLabel(user)} 선택` }}
                                                  onClick={(event) => event.stopPropagation()}
                                                  onChange={() => toggleRecipientUser(user)}
                                                />
                                                <Typography sx={recipientOptionTextSx} noWrap>{getRecipientUserLabel(user)}</Typography>
                                              </Box>
                                            );
                                          })
                                        ) : (
                                          <Typography sx={keywordEmptySx}>검색된 사용자가 없습니다.</Typography>
                                        )}
                                      </Stack>
                                    </Paper>
                                  )}
                                </Box>
                              </ClickAwayListener>
                              <Box sx={selectedRecipientWellSx}>
                                <Stack direction="row" sx={selectedRecipientListSx}>
                                  {draft.notificationRecipientUsers.length ? (
                                    draft.notificationRecipientUsers.map((user) => (
                                      <Chip
                                        key={user.sk}
                                        label={getRecipientUserLabel(user)}
                                        size="small"
                                        onDelete={() => toggleRecipientUser(user)}
                                        deleteIcon={<ChipDeleteIcon />}
                                        sx={selectedRecipientChipSx}
                                      />
                                    ))
                                  ) : (
                                    <Typography sx={selectedRecipientEmptySx}>추가된 사용자가 없습니다</Typography>
                                  )}
                                </Stack>
                              </Box>
                              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={recipientHelperRowSx}>
                                <Typography sx={recipientHelperTextSx}>위 선택한 사용자의 사번으로 Teams 알림을 전송합니다.</Typography>
                                {draft.notificationRecipientUsers.length > 0 && (
                                  <Button size="small" sx={recipientClearButtonSx} onClick={clearRecipientUsers}>
                                    전체 삭제
                                  </Button>
                                )}
                              </Stack>
                            </Box>
                          )}
                        </Box>

                        {draft.sentimentEnabled && (
                          <Box sx={notificationConditionGroupSx}>
                            <Typography sx={notificationPanelLabelSx}>알림 조건</Typography>
                            <Stack spacing={1.25}>
                              <SettingSwitch
                                title="부정 기사 발생 시 알림"
                                desc="부정 판정 결과가 부정인 기사만 알립니다."
                                checked={draft.notifyOnNegative}
                                onChange={(checked) => updateDraft("notifyOnNegative", checked)}
                              />
                            </Stack>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={sectionSx(activeStep === 3)}>
              <SectionHeader index={draft.type === "KEYWORD" ? 4 : 3} title="얼마나 자주 확인하고 보관할까요?" />
              <Box sx={{ p: 3, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2 }}>
                <Box>
                  <Typography sx={fieldLabelSx}>실행 주기</Typography>
                  <TextField
                    select
                    size="small"
                    value={draft.intervalMinutes}
                    onChange={(event) => updateDraft("intervalMinutes", Number(event.target.value))}
                    inputProps={{ "aria-label": "실행 주기" }}
                    SelectProps={{ IconComponent: MonitoringSelectIcon }}
                    sx={scheduleFieldSx}
                    fullWidth
                  >
                    {[5, 10, 15, 30, 60].map((minute) => (
                      <MenuItem key={minute} value={minute}>{minute}분마다</MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box>
                  <Typography sx={fieldLabelSx}>기사 저장 기간(일)</Typography>
                  <TextField
                    select
                    size="small"
                    value={draft.retentionDays}
                    onChange={(event) => updateDraft("retentionDays", Math.min(7, Math.max(1, Number(event.target.value) || 1)))}
                    inputProps={{ "aria-label": "기사 저장 기간(일)" }}
                    SelectProps={{ IconComponent: MonitoringSelectIcon }}
                    sx={scheduleFieldSx}
                    fullWidth
                  >
                    {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                      <MenuItem key={day} value={day}>{day}일</MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box>
                  <Typography sx={fieldLabelSx}>실행 종료일</Typography>
                  <TextField
                    size="small"
                    type="date"
                    value={draft.activeUntilDate}
                    onChange={(event) => updateDraft("activeUntilDate", event.target.value)}
                    helperText={draft.activeUntilDate ? "해당 날짜 23:59까지 실행합니다." : "비워두면 무기한 활성화됩니다."}
                    inputProps={{ min: getTodayKstDate(), "aria-label": "실행 종료일" }}
                    sx={scheduleFieldSx}
                    fullWidth
                  />
                </Box>
              </Box>
            </Paper>
          </Stack>

          <Paper variant="outlined" sx={{ ...panelSx, position: { lg: "sticky" }, top: 16 }}>
            <PanelHeader icon={<ImageIcon src={reviewCheckIconSrc} alt="" size={20} />} title={isEditMode ? "저장 전 확인" : "생성 전 확인"} right={<Chip label={canCreate ? "준비됨" : "확인 필요"} size="small" sx={canCreate ? successChipSx : warningChipSx} />} />
            <Stack spacing={3} sx={{ p: 3 }}>
              <Stack spacing={1} sx={reviewListSx}>
                <ReviewRow label="이름" value={draft.title || "미입력"} />
                <ReviewRow label="설명" value={draft.description.trim() || "없음"} />
                <ReviewRow label="방식" value={draft.type === "KEYWORD" ? "키워드 기반" : "보도자료 기반"} />
                <ReviewRow label="검색어" value={draft.type === "KEYWORD" ? (draft.searchTermRefs.length ? draft.searchTerms.join(", ") : "선택 필요") : "보도자료 연관 기사 기준"} />
                {draft.type === "KEYWORD" && <ReviewSearchTermExcludeSummary rows={selectedSearchTermExcludeRows} />}
                <ReviewRow label="판정" value={getSentimentJudgementLabel(draft)} />
                {draft.sentimentEnabled && (
                  <ReviewRow label="부정 키워드" value={getKeywordSummary(effectiveNegativeKeywords)} />
                )}
                <ReviewRow label="알림" value={draft.notificationEnabled ? getNotificationAudienceLabel(draft) : "알림 없음"} />
                <ReviewRow label="알림 조건" value={getNotificationConditionLabel(draft)} />
                <ReviewRow label="주기" value={`${draft.intervalMinutes}분마다 실행`} />
                <ReviewRow label="보관" value={`${draft.retentionDays}일 저장`} />
                <ReviewRow label="실행 기간" value={draft.activeUntilDate ? `${draft.activeUntilDate}까지` : "무기한"} />
              </Stack>
              {created && (
                <Box sx={{ p: 1.25, borderRadius: "8px", bgcolor: "#ecfdf3", border: "1px solid #abefc6" }}>
                  <Typography sx={{ color: "#065f46", fontSize: 12, fontWeight: 600 }}>{statusMessage ?? (isEditMode ? "모니터링 설정이 저장되었습니다." : "모니터링이 생성되었습니다.")}</Typography>
                </Box>
              )}
              {!created && statusMessage && (
                <Box sx={{ p: 1.25, borderRadius: "8px", bgcolor: "#fff7ed", border: "1px solid #fed7aa" }}>
                  <Typography sx={{ color: "#9a3412", fontSize: 12, fontWeight: 600 }}>{statusMessage}</Typography>
                </Box>
              )}
              <Button
                fullWidth
                startIcon={<AddIcon />}
                variant="contained"
                disabled={!canCreate || saving || addingSearchTerm}
                onClick={createMonitoring}
                sx={primaryButtonSx}
              >
                {saving ? (isEditMode ? "저장 중" : "생성 중") : isEditMode ? "설정 저장" : "모니터링 생성하기"}
              </Button>
            </Stack>
          </Paper>
        </Box>
      </Stack>
      <SearchTermAddDialog
        open={searchTermAddModalOpen}
        value={searchTermInput}
        excludeInput={searchTermExcludeInput}
        excludeTerms={searchTermExcludeTerms}
        enabled={searchTermAddEnabled}
        autoFocusInput={searchTermAddAutoFocus}
        adding={addingSearchTerm}
        duplicate={Boolean(activeDuplicateSearchTerm)}
        onValueChange={setSearchTermInput}
        onExcludeInputChange={updateSearchTermExcludeInput}
        onAddExcludeTerms={addSearchTermExcludeInput}
        onDeleteExcludeTerm={deleteSearchTermExcludeTerm}
        onEnabledChange={setSearchTermAddEnabled}
        onReleaseSubmitLock={releaseSearchTermAddSubmitLock}
        onClose={closeSearchTermAddModal}
        onSubmit={submitSearchTermAddModal}
      />
    </Box>
  );
};

const steps = [
  { title: "방식", desc: "보도자료 또는 키워드 기반" },
  { title: "기사 찾기", desc: "이름과 검색어 선택" },
  { title: "판단과 알림", desc: "부정 판정과 알림 조건" },
  { title: "주기와 보관", desc: "실행 주기와 저장 기간" },
];

const getRecipientUserLabel = (user: NotificationRecipientUser) => {
  const meta = [user.dept, user.company, user.sk].filter(Boolean).join(" · ");
  return `${user.name || user.sk}${meta ? ` (${meta})` : ""}`;
};

const getRecipientUserSearchText = (user: NotificationRecipientUser) =>
  [user.sk, user.name, user.company, user.dept, getRecipientUserLabel(user)].filter(Boolean).join(" ").toLowerCase();

const getNotificationAudienceLabel = (draft: Pick<MonitoringDraft, "notificationAudience" | "notificationRecipientUsers">) => {
  if (draft.notificationAudience === "ALL") return "홍보실 전체";
  if (draft.notificationAudience === "CUSTOM") return `지정 사용자 ${draft.notificationRecipientUsers.length}명`;
  return "나에게만 전송";
};

const getNotificationConditionLabel = (draft: Pick<MonitoringDraft, "notificationEnabled" | "notifyOnNegative">) => {
  if (!draft.notificationEnabled) return "알림 없음";
  const labels = [];
  if (draft.notifyOnNegative) labels.push("부정 기사");
  return labels.length ? labels.join(" + ") : "알림 조건 없음";
};

const getSentimentJudgementLabel = (draft: Pick<MonitoringDraft, "sentimentEnabled" | "sentimentKeywordMode">) => {
  if (!draft.sentimentEnabled) return "판정 안 함";
  return draft.sentimentKeywordMode === "CUSTOM" ? "부정 판정 사용 (맞춤 키워드)" : "부정 판정 사용 (전역 키워드)";
};

const getKeywordSummary = (keywords: string[]) => keywords.length ? keywords.join(", ") : "없음";

const getTodayKstDate = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

// 단계별 필수 입력이 완료되었는지 계산해 좌측 진행 상태에 표시한다.
const getStepComplete = (index: number, draft: MonitoringDraft, pressReleaseMonitoringAvailable: boolean) => {
  const hasTitle = Boolean(draft.title.trim());
  if (index === 0) return hasTitle && (draft.type === "KEYWORD" || pressReleaseMonitoringAvailable);
  if (index === 1) return Boolean(draft.title.trim() && (draft.type !== "KEYWORD" || draft.searchTermRefs.length));
  if (index === 2) return !draft.notificationEnabled || (draft.notificationAudience !== "CUSTOM" || draft.notificationRecipientUsers.length > 0);
  return draft.intervalMinutes > 0 && draft.retentionDays > 0 && draft.retentionDays <= 7;
};

const settingsStepsIconSrc = "/assets/monitoring/settings-steps.png";
const keywordAddIconSrc = "/assets/monitoring/keyword-add.png";
const stepCompleteIconSrc = "/assets/monitoring/step-complete.png";
const reviewCheckIconSrc = "/assets/monitoring/review-check.png";
const monitoringSelectArrowIconSrc = `${process.env.PUBLIC_URL}/assets/monitoring-list/icon-select-arrow.png`;

const ImageIcon = ({ src, alt, size = 16 }: { src: string; alt: string; size?: number }) => (
  <Box
    component="img"
    src={src}
    alt={alt}
    sx={{ width: size, height: size, display: "block", flex: "0 0 auto" }}
  />
);

type MonitoringSelectIconProps = React.ImgHTMLAttributes<HTMLImageElement> & { ownerState?: unknown };
const MonitoringSelectIcon = ({ ownerState, style, ...props }: MonitoringSelectIconProps) => {
  void ownerState;
  return (
    <Box
      component="img"
      {...props}
      src={monitoringSelectArrowIconSrc}
      alt=""
      aria-hidden="true"
      draggable={false}
      style={style}
      sx={selectArrowIconSx}
    />
  );
};

const checkboxEmptyIconSx = {
  width: 16,
  height: 16,
  borderRadius: "4px",
  bgcolor: "#E5E5E5",
  display: "block",
};
const checkboxCheckedIconSx = {
  ...checkboxEmptyIconSx,
  bgcolor: "#5D88F1",
  position: "relative",
};
const checkboxCheckMarkSx = {
  position: "absolute",
  left: 4,
  top: 4,
  width: 8,
  height: 5,
  borderLeft: "1.5px solid #fff",
  borderBottom: "1.5px solid #fff",
  transform: "rotate(-45deg)",
};
const checkboxIndeterminateMarkSx = {
  position: "absolute",
  left: 4,
  top: 7,
  width: 8,
  height: 0,
  borderTop: "1.5px solid #fff",
};
const radioEmptyIconSx = {
  width: 16,
  height: 16,
  borderRadius: "50%",
  bgcolor: "#D4D4D4",
  display: "block",
};
const radioCheckedIconSx = {
  ...radioEmptyIconSx,
  bgcolor: "#5D88F1",
  position: "relative",
};
const radioCheckedDotSx = {
  position: "absolute",
  left: 4,
  top: 4,
  width: 8,
  height: 8,
  borderRadius: "50%",
  bgcolor: "#fff",
};
const chipDeleteIconSx = {
  position: "relative",
  width: 24,
  height: 24,
  flex: "0 0 auto",
  opacity: 0.6,
  borderRadius: "12px",
  display: "block",
  cursor: "pointer",
};
const chipDeleteLineSx = (deg: number) => ({
  position: "absolute",
  left: 8,
  top: 11,
  width: 11.31,
  height: 0,
  borderTop: "1.5px solid #DE3434",
  color: "#DE3434",
  transform: `rotate(${deg}deg)`,
  transformOrigin: "center",
});

const EmptyCheckboxIcon = () => <Box component="span" sx={checkboxEmptyIconSx} />;

const CheckedCheckboxIcon = () => (
  <Box component="span" sx={checkboxCheckedIconSx}>
    <Box component="span" sx={checkboxCheckMarkSx} />
  </Box>
);

const IndeterminateCheckboxIcon = () => (
  <Box component="span" sx={checkboxCheckedIconSx}>
    <Box component="span" sx={checkboxIndeterminateMarkSx} />
  </Box>
);

const EmptyRadioIcon = () => <Box component="span" sx={radioEmptyIconSx} />;

const CheckedRadioIcon = () => (
  <Box component="span" sx={radioCheckedIconSx}>
    <Box component="span" sx={radioCheckedDotSx} />
  </Box>
);

const ChipDeleteIcon = (props: React.HTMLAttributes<HTMLSpanElement>) => (
  <Box component="span" {...props} sx={chipDeleteIconSx}>
    <Box component="span" sx={chipDeleteLineSx(45)} />
    <Box component="span" sx={chipDeleteLineSx(-45)} />
  </Box>
);

const PanelHeader = ({ icon, title, right }: { icon: React.ReactNode; title: string; right?: React.ReactNode }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 3, pt: 3, pb: 0, gap: 1 }}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <Box sx={{ color: "#94a3b8", display: "flex" }}>{icon}</Box>
      <Typography sx={cardTitleSx} noWrap>{title}</Typography>
    </Stack>
    {right}
  </Stack>
);

const SectionHeader = ({ index, title }: { index: number; title: string }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 3, pt: 3, pb: 1 }}>
    <Box sx={stepNumberSx}>
      {index}
    </Box>
    <Typography sx={cardTitleSx}>{title}</Typography>
  </Stack>
);

const StepButton = ({
  active,
  complete,
  index,
  title,
  desc,
  onClick,
}: {
  active: boolean;
  complete: boolean;
  index: number;
  title: string;
  desc: string;
  onClick: () => void;
}) => (
  <Box
    onClick={onClick}
    sx={{
      p: 2,
      height: 76,
      borderRadius: "8px",
      border: active ? "1px solid #2E3341" : "1px solid #DDE0EA",
      bgcolor: "#fff",
      boxSizing: "border-box",
      cursor: "pointer",
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ height: "100%" }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
        <Box sx={stepNumberSx}>{index}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: "#2E3341", fontSize: 16, fontWeight: 600, lineHeight: "24px" }}>{title}</Typography>
          <Typography sx={{ color: "#6F7279", fontSize: 14, lineHeight: "20px" }}>{desc}</Typography>
        </Box>
      </Stack>
      {complete && <ImageIcon src={stepCompleteIconSrc} alt="완료" size={24} />}
    </Stack>
  </Box>
);

const ChoiceCard = ({
  selected,
  disabled,
  title,
  badge,
  desc,
  onClick,
}: {
  selected: boolean;
  disabled?: boolean;
  title: string;
  badge?: string;
  desc: string;
  onClick: () => void;
}) => (
  <Box
    onClick={disabled ? undefined : onClick}
    sx={choiceCardSx(selected, disabled)}
  >
    <Box sx={choiceRadioSx(selected)}>{selected && <Box sx={choiceRadioDotSx} />}</Box>
    <Box sx={choiceTextSx(disabled)}>
      <Typography sx={choiceTitleSx}>{title}</Typography>
      <Typography sx={choiceDescSx}>{desc}</Typography>
    </Box>
    {badge && <Box sx={choiceBadgeSx}>{badge}</Box>}
  </Box>
);

const SettingSwitch = ({ title, desc, checked, onChange }: { title: string; desc: string; checked: boolean; onChange: (checked: boolean) => void }) => (
  <Box sx={settingSwitchBoxSx}>
    <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" sx={settingSwitchInnerSx}>
      <Box sx={settingSwitchTextBoxSx}>
        <Typography sx={settingSwitchTitleSx}>{title}</Typography>
        <Typography sx={settingSwitchDescSx}>{desc}</Typography>
      </Box>
      <Stack direction="row" alignItems="center" sx={switchControlSx}>
        <Typography sx={checked ? switchLabelOnSx : switchLabelOffSx}>{checked ? "사용" : "미사용"}</Typography>
        <Switch checked={checked} onChange={(event) => onChange(event.target.checked)} sx={figmaSwitchSx} />
      </Stack>
    </Stack>
  </Box>
);

const SentimentKeywordEditor = ({
  label,
  keywords,
  inputValue,
  editing,
  onInputChange,
  onSubmit,
  onEdit,
  onDelete,
}: {
  label: string;
  keywords: string[];
  inputValue: string;
  editing: boolean;
  onInputChange: (value: string) => void;
  onSubmit: () => void;
  onEdit: (index: number, keyword: string) => void;
  onDelete: (index: number) => void;
}) => (
  <Box sx={sentimentKeywordEditorSx}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography sx={negativeKeywordSectionTitleSx}>{label}</Typography>
        <Chip label={`${keywords.length}개`} size="small" sx={negativeKeywordCountChipSx} />
      </Stack>
    </Stack>
    <Stack direction={{ xs: "column", sm: "row" }} sx={negativeKeywordInputRowSx}>
      <TextField
        size="small"
        value={inputValue}
        placeholder={`${label}를 입력해주세요.`}
        inputProps={{ "aria-label": label }}
        onChange={(event) => onInputChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            onSubmit();
          }
        }}
        sx={negativeKeywordInputSx}
      />
      <Button
        size="small"
        variant="outlined"
        startIcon={<AddIcon sx={negativeKeywordAddIconSx} />}
        sx={negativeKeywordAddButtonSx}
        disabled={!normalizeSentimentKeywordText(inputValue)}
        onClick={onSubmit}
      >
        {editing ? "수정" : "추가"}
      </Button>
    </Stack>
    <Box sx={negativeKeywordSelectedWellSx}>
      <Stack direction="row" spacing={0.5} sx={sentimentKeywordChipListSx}>
        {keywords.length ? (
          keywords.map((keyword, index) => (
            <Chip
              key={`${keyword}-${index}`}
              label={keyword}
              size="small"
              onClick={() => onEdit(index, keyword)}
              onDelete={() => onDelete(index)}
              deleteIcon={<ChipDeleteIcon />}
              sx={negativeChipSx}
            />
          ))
        ) : (
          <Typography sx={negativeKeywordEmptySx}>추가된 키워드가 없습니다</Typography>
        )}
      </Stack>
    </Box>
  </Box>
);

const KeywordGroup = ({ label, keywords, empty }: { label: string; keywords: string[]; empty: string }) => (
  <Box sx={negativeKeywordPanelSx}>
    <Typography sx={negativeKeywordTitleSx}>{label}</Typography>
    <Stack direction="row" spacing={0.5} sx={negativeKeywordChipListSx}>
      {keywords.length ? (
        keywords.map((keyword) => <Chip key={keyword} label={keyword} size="small" sx={negativeChipSx} />)
      ) : (
        <Typography sx={negativeKeywordEmptySx}>{empty}</Typography>
      )}
    </Stack>
  </Box>
);

const SearchTermAddDialog = ({
  open,
  value,
  excludeInput,
  excludeTerms,
  enabled,
  autoFocusInput,
  adding,
  duplicate,
  onValueChange,
  onExcludeInputChange,
  onAddExcludeTerms,
  onDeleteExcludeTerm,
  onEnabledChange,
  onReleaseSubmitLock,
  onClose,
  onSubmit,
}: {
  open: boolean;
  value: string;
  excludeInput: string;
  excludeTerms: string[];
  enabled: boolean;
  autoFocusInput: boolean;
  adding: boolean;
  duplicate: boolean;
  onValueChange: (value: string) => void;
  onExcludeInputChange: (value: string) => void;
  onAddExcludeTerms: () => void;
  onDeleteExcludeTerm: (term: string) => void;
  onEnabledChange: (enabled: boolean) => void;
  onReleaseSubmitLock: () => void;
  onClose: () => void;
  onSubmit: () => void;
}) => {
  const handleDialogClose = (_event: unknown, reason: "backdropClick" | "escapeKeyDown") => {
    if (adding || reason === "backdropClick" || reason === "escapeKeyDown") return;
    onClose();
  };
  const releaseSubmitLockOnEnterKeyUp = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") onReleaseSubmitLock();
  };
  const canAddExcludeTerm = normalizeExcludeTerms(excludeInput).length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: keywordDialogPaperSx }}
      onPointerDownCapture={onReleaseSubmitLock}
      onKeyUpCapture={releaseSubmitLockOnEnterKeyUp}
    >
      <DialogTitle sx={keywordDialogTitleSx}>키워드 추가</DialogTitle>
      <DialogContent sx={keywordDialogContentSx}>
        <Box sx={keywordDialogFieldGroupSx}>
          <Typography sx={keywordDialogFieldLabelSx}>키워드 구분</Typography>
          <RadioGroup row value="SEARCH" sx={keywordDialogRadioGroupSx}>
            <FormControlLabel
              value="SEARCH"
              control={<Radio size="small" icon={<EmptyRadioIcon />} checkedIcon={<CheckedRadioIcon />} sx={figmaRadioSx} />}
              label="검색 키워드"
              sx={keywordDialogRadioLabelSx}
            />
            <FormControlLabel
              value="NEGATIVE"
              disabled
              control={<Radio size="small" icon={<EmptyRadioIcon />} checkedIcon={<CheckedRadioIcon />} sx={figmaRadioSx} />}
              label="부정 키워드"
              sx={keywordDialogRadioLabelSx}
            />
          </RadioGroup>
        </Box>
        <Box sx={keywordDialogFieldGroupSx}>
          <Typography sx={keywordDialogFieldLabelSx}>추가할 키워드</Typography>
          <TextField
            autoFocus={autoFocusInput}
            size="small"
            value={value}
            onChange={(event) => {
              onReleaseSubmitLock();
              onValueChange(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                if (event.nativeEvent.isComposing) return;
                event.preventDefault();
                event.stopPropagation();
                onSubmit();
              }
            }}
            disabled={adding}
            inputProps={{ "aria-label": "추가할 키워드" }}
            sx={keywordDialogTextFieldSx}
          />
        </Box>
        <Box sx={keywordDialogExcludeSectionSx}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={keywordDialogExcludeHeaderSx}>
            <Typography sx={keywordDialogFieldLabelSx}>제외 키워드</Typography>
            <Box sx={keywordDialogCountTagSx}>{excludeTerms.length}개</Box>
          </Stack>
          <Stack direction="row" sx={keywordDialogExcludeInputRowSx}>
            <TextField
              size="small"
              value={excludeInput}
              placeholder="쉼표, 줄바꿈 또는 Enter로 추가"
              onChange={(event) => {
                onReleaseSubmitLock();
                onExcludeInputChange(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === ",") {
                  if (event.nativeEvent.isComposing) return;
                  event.preventDefault();
                  event.stopPropagation();
                  onAddExcludeTerms();
                }
              }}
              disabled={adding}
              inputProps={{ "aria-label": "제외 키워드" }}
              sx={keywordDialogTextFieldSx}
            />
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                onReleaseSubmitLock();
                onAddExcludeTerms();
              }}
              disabled={!canAddExcludeTerm || adding}
              sx={keywordDialogExcludeAddButtonSx}
            >
              추가
            </Button>
          </Stack>
          <Box sx={keywordDialogExcludeWellSx(excludeTerms.length > 0)}>
            {excludeTerms.length ? (
              <Stack direction="row" sx={keywordDialogExcludeChipListSx}>
                {excludeTerms.map((term) => (
                  <Chip
                    key={term}
                    label={term}
                    onDelete={() => onDeleteExcludeTerm(term)}
                    deleteIcon={<ChipDeleteIcon />}
                    sx={keywordDialogExcludeChipSx}
                  />
                ))}
              </Stack>
            ) : (
              <Typography sx={keywordDialogExcludeEmptySx}>제외 키워드가 없습니다</Typography>
            )}
          </Box>
        </Box>
        <Stack direction="row" alignItems="center" sx={keywordDialogEnabledRowSx}>
          <Typography sx={enabled ? keywordDialogEnabledLabelSx : keywordDialogDisabledLabelSx}>
            {enabled ? "활성" : "비활성"}
          </Typography>
          <Switch
            checked={enabled}
            onChange={(event) => {
              onReleaseSubmitLock();
              onEnabledChange(event.target.checked);
            }}
            disabled={adding}
            sx={figmaSwitchSx}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={keywordDialogActionsSx}>
        <Button onClick={onClose} disabled={adding} sx={keywordDialogActionOutlineButtonSx}>취소</Button>
        <Button
          variant="contained"
          onClick={() => {
            onReleaseSubmitLock();
            onSubmit();
          }}
          disabled={!normalizeSearchTermText(value) || adding}
          sx={keywordDialogActionPrimaryButtonSx}
        >
          {adding ? "추가 중" : duplicate ? "기존 키워드 선택" : "키워드 추가"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SearchTermExcludeRow = ({ row }: { row: SearchTermExcludeDisplayRow }) => (
  <Box sx={searchTermExcludeRowSx}>
    <Typography sx={searchTermExcludeTermSx} noWrap>{row.term}</Typography>
    <Stack direction="row" spacing={0.45} sx={searchTermExcludeChipListSx}>
      {row.excludeTerms.length ? (
        row.excludeTerms.map((term) => <Chip key={`${row.term}-${term}`} label={term} size="small" sx={excludeTermChipSx} />)
      ) : (
        <Typography sx={searchTermExcludeEmptySx}>없음</Typography>
      )}
    </Stack>
  </Box>
);

const ReviewRow = ({ label, value }: { label: string; value: string }) => (
  <Box sx={reviewRowSx}>
    <Typography sx={reviewLabelSx}>{label}</Typography>
    <Typography sx={reviewValueSx}>{value}</Typography>
  </Box>
);

const ReviewSearchTermExcludeSummary = ({ rows }: { rows: SearchTermExcludeDisplayRow[] }) => (
  <Box sx={reviewRowSx}>
    <Typography sx={reviewLabelSx}>제외어 적용</Typography>
    <Stack spacing={0.55}>
      {rows.length ? (
        rows.map((row) => <SearchTermExcludeRow key={getSearchTermRefKey(row)} row={row} />)
      ) : (
        <Typography sx={searchTermExcludeEmptySx}>선택 필요</Typography>
      )}
    </Stack>
  </Box>
);

const figmaShadow = "0px 6px 14px rgba(68, 100, 178, 0.08)";

const pageSx = { minHeight: "100%", bgcolor: "#F2F4F8", p: { xs: 2, md: 4 } };
const contentShellSx = { maxWidth: 1518, mx: "auto", width: "100%" };
const createLayoutSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    lg: "240px minmax(0, 1fr) 260px",
    xl: "383px 720px 383px",
  },
  gap: 2,
  alignItems: "start",
  justifyContent: "center",
};
const pageTitleSx = { color: "#2E3341", fontSize: 24, fontWeight: 700, lineHeight: "32px" };
const pageSubtitleSx = { mt: 0.5, color: "#2E3341", fontSize: 16, lineHeight: "24px" };
const cardTitleSx = { color: "#000", fontSize: 16, fontWeight: 600, lineHeight: "22px" };

const panelSx = {
  borderRadius: "12px",
  border: "1px solid transparent",
  bgcolor: "#fff",
  boxShadow: figmaShadow,
  overflow: "hidden",
};

const sectionSx = (active: boolean) => ({
  ...panelSx,
  borderColor: active ? "#2E3341" : "transparent",
});

const keywordSectionSx = (active: boolean, expanded: boolean) => ({
  ...sectionSx(active),
  position: "relative",
  overflow: "visible",
  zIndex: expanded ? 10 : active ? 2 : 1,
});

const processingSectionSx = (active: boolean, expanded: boolean) => ({
  ...sectionSx(active),
  position: "relative",
  overflow: "visible",
  zIndex: expanded ? 9 : active ? 2 : 1,
});

const stepNumberSx = {
  width: 24,
  height: 24,
  flex: "0 0 auto",
  borderRadius: "4px",
  bgcolor: "#2E3341",
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "14px",
};

const stepConnectorSx = {
  display: "flex",
  justifyContent: "center",
  color: "#5D88F1",
  my: 0,
};
const fieldLabelSx = { color: "#2E3341", fontSize: 13, fontWeight: 600, lineHeight: "20px", mb: 0.5 };
const keywordLabelRowSx = { mb: 0.5 };
const keywordPickerSx = {
  position: "relative",
  zIndex: 2,
};
const keywordInputSx = {
  "& .MuiOutlinedInput-root": {
    height: 48,
    borderRadius: "8px",
    bgcolor: "#fff",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    minHeight: 48,
    px: 2,
    fontSize: 16,
    lineHeight: "24px",
    letterSpacing: "-0.015em",
  },
  "& .MuiInputBase-input": {
    height: 24,
    p: 0,
    color: "#2E3341",
    lineHeight: "24px",
    minWidth: 0,
    "&::placeholder": {
      color: "#A3A3A3",
      opacity: 1,
    },
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#DDE0EA" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#C8CDD9" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2E3341" },
};
const keywordDropdownSx = (optionCount: number) => {
  const visibleOptionCount = Math.max(1, Math.min(optionCount, 10));
  const headerHeight = 41;
  const optionListHeight = optionCount > 0 ? visibleOptionCount * 24 + Math.max(0, visibleOptionCount - 1) * 12 : 48;
  const height = Math.min(436, 32 + headerHeight + optionListHeight + 16);
  return {
    position: "absolute",
    top: "calc(100% + 12px)",
    left: 0,
    right: 0,
    zIndex: 6,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    height,
    px: 3,
    py: 2,
    borderRadius: "8px",
    border: "1px solid #DDE0EA",
    bgcolor: "#fff",
    boxShadow: "0px 10px 18px rgba(68, 100, 178, 0.16)",
    overflow: "hidden",
  };
};
const keywordSelectArrowSx = (open: boolean) => ({
  width: 12,
  height: 7,
  objectFit: "contain",
  flex: "0 0 auto",
  pointerEvents: "none",
  transform: open ? "rotate(180deg)" : "none",
});
const selectArrowIconSx = {
  width: 12,
  height: 7,
  right: "16px !important",
  top: "calc(50% - 3.5px) !important",
  objectFit: "contain",
  pointerEvents: "none",
};
const keywordDropdownHeaderSx = {
  minHeight: 24,
  pb: 2,
  borderBottom: "1px solid #DDE0EA",
};
const keywordDropdownCountSx = { color: "#2E3341", fontSize: 14, fontWeight: 500, lineHeight: "22px", letterSpacing: "-0.015em" };
const keywordSelectAllTextSx = {
  color: "#2E3341",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "22px",
  letterSpacing: "-0.015em",
};
const keywordOptionListSx = {
  maxHeight: 348,
  flex: "1 1 auto",
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
  p: 0,
  pr: 1,
  scrollbarColor: "#A8A8A8 transparent",
  scrollbarGutter: "stable",
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": { width: "6px", height: "6px" },
  "&::-webkit-scrollbar-track": { bgcolor: "transparent", my: 0 },
  "&::-webkit-scrollbar-thumb": { bgcolor: "#A8A8A8", borderRadius: "3px", minHeight: "195px" },
  "&::-webkit-scrollbar-corner": { bgcolor: "transparent" },
};
const keywordOptionRowSx = {
  minHeight: 24,
  p: 0,
  display: "flex",
  alignItems: "center",
  gap: 1,
  cursor: "pointer",
  "&:hover": { bgcolor: "transparent" },
};
const figmaCheckboxSx = {
  width: 16,
  height: 16,
  p: 0,
  flex: "0 0 auto",
  color: "transparent",
  "&:hover": { bgcolor: "transparent" },
};
const autocompleteOptionTextSx = { color: "#2E3341", fontSize: 16, fontWeight: 400, lineHeight: "24px", letterSpacing: "-0.015em" };
const keywordEmptySx = { px: 2, py: 2, color: "#6F7279", fontSize: 14, textAlign: "center" };
const recipientOptionTextSx = { ...autocompleteOptionTextSx, minWidth: 0 };

const settingSwitchBoxSx = {
  p: 2,
  height: 74,
  borderRadius: "8px",
  bgcolor: "#FAFAFA",
  boxSizing: "border-box",
};

const settingSwitchInnerSx = {
  height: "100%",
  gap: 5,
};

const settingSwitchTextBoxSx = {
  minWidth: 0,
  flex: "1 1 auto",
  height: 42,
};

const settingSwitchTitleSx = {
  color: "#000",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "22px",
};

const settingSwitchDescSx = {
  color: "#6F7279",
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "20px",
};

const switchControlSx = {
  flex: "0 0 auto",
  gap: 1,
  minWidth: 84,
  justifyContent: "flex-end",
};

const switchLabelOffSx = {
  color: "#6F7279",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "22px",
  letterSpacing: "-0.015em",
};

const switchLabelOnSx = {
  ...switchLabelOffSx,
  color: "#2E3341",
};

const figmaSwitchSx = {
  width: 40,
  height: 24,
  p: 0,
  "& .MuiSwitch-switchBase": {
    p: "4px",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        bgcolor: "#5D88F1",
        opacity: 1,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    width: 16,
    height: 16,
    boxShadow: "none",
  },
  "& .MuiSwitch-track": {
    borderRadius: 20,
    bgcolor: "#D0D0D0",
    opacity: 1,
  },
};

const notificationRadioGroupSx = {
  minHeight: 40,
  gap: 3,
  alignItems: "center",
  boxSizing: "border-box",
};
const figmaRadioSx = {
  width: 16,
  height: 16,
  p: 0,
  color: "transparent",
  "&:hover": { bgcolor: "transparent" },
};
const notificationRadioLabelSx = {
  m: 0,
  gap: 1,
  height: 20,
  color: "#2E3341",
  "& .MuiFormControlLabel-label": {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: "20px",
    letterSpacing: "-0.015em",
    color: "#2E3341",
  },
};

const configSectionSx = {
  p: 0,
  borderRadius: "8px",
  border: "none",
  bgcolor: "transparent",
};

const configSectionTitleSx = {
  color: "#2E3341",
  fontSize: 13,
  fontWeight: 600,
  lineHeight: "20px",
  mb: 0.5,
};

const selectedKeywordWellSx = {
  p: 2,
  minHeight: 52,
  borderRadius: "8px",
  border: "1px dashed #DDE0EA",
  bgcolor: "#FAFAFA",
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
};
const searchConditionGroupSx = {
  gap: 0.5,
};
const selectedSearchTermListSx = {
  flexWrap: "wrap",
  gap: 1,
  minHeight: 20,
  maxHeight: 96,
  overflowY: "auto",
  alignItems: "center",
  alignContent: "flex-start",
  width: "100%",
};
const selectedSearchTermEmptySx = {
  width: "100%",
  color: "#6F7279",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "20px",
  textAlign: "center",
};
const selectedSearchTermChipSx = {
  height: 32,
  borderRadius: "16px",
  bgcolor: "#EAEAEA",
  color: "#2E3341",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "14px",
  "& .MuiChip-label": {
    px: 1.5,
    pr: 0.5,
  },
  "& .MuiChip-deleteIcon": {
    m: 0,
    mr: 0.5,
  },
};
const searchTermExcludeRowSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "110px minmax(0, 1fr)" },
  gap: 0.6,
  alignItems: "center",
  minWidth: 0,
};
const searchTermExcludeTermSx = { color: "#2E3341", fontSize: 13, fontWeight: 600, minWidth: 0 };
const searchTermExcludeChipListSx = { flexWrap: "wrap", rowGap: 0.45, minWidth: 0 };
const searchTermExcludeEmptySx = { color: "#6F7279", fontSize: 13, lineHeight: "20px" };

const reviewRowSx = {
  display: "grid",
  gridTemplateColumns: "51px minmax(0, 1fr)",
  columnGap: 6,
  alignItems: "start",
};
const reviewListSx = { width: "100%" };
const reviewLabelSx = { color: "#2E3341", fontSize: 14, fontWeight: 600, lineHeight: "22px", letterSpacing: "-0.015em" };
const reviewValueSx = { color: "#6F7279", fontSize: 14, fontWeight: 400, lineHeight: "22px", letterSpacing: "-0.015em", wordBreak: "keep-all" };

const keywordDialogPaperSx = {
  width: 560,
  maxWidth: "calc(100vw - 32px)",
  borderRadius: "24px",
  boxShadow: "0px 6px 14px rgba(68, 100, 178, 0.08)",
  overflow: "hidden",
  m: 2,
};
const keywordDialogTitleSx = {
  px: 5,
  pt: 5,
  pb: 0,
  minHeight: 66,
  boxSizing: "border-box",
  color: "#000",
  fontSize: 20,
  fontWeight: 600,
  lineHeight: "26px",
};
const keywordDialogContentSx = {
  px: 5,
  py: 3,
  display: "flex",
  flexDirection: "column",
  gap: 3,
  overflow: "visible",
};
const keywordDialogActionsSx = {
  px: 5,
  pb: 5,
  pt: 0,
  gap: 1,
  height: 80,
  boxSizing: "border-box",
  alignItems: "flex-start",
  justifyContent: "flex-end",
};

const keywordDialogFieldGroupSx = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 0.5,
};
const keywordDialogFieldLabelSx = {
  color: "#2E3341",
  fontSize: 13,
  fontWeight: 600,
  lineHeight: "20px",
};
const keywordDialogRadioGroupSx = {
  gap: 3,
  height: 40,
  alignItems: "center",
};
const keywordDialogRadioLabelSx = {
  m: 0,
  gap: 1,
  height: 40,
  color: "#2E3341",
  "& .MuiFormControlLabel-label": {
    color: "#2E3341",
    fontSize: 16,
    fontWeight: 500,
    lineHeight: "24px",
    letterSpacing: "-0.015em",
  },
  "&.Mui-disabled .MuiFormControlLabel-label": {
    color: "#2E3341",
  },
};
const keywordDialogTextFieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: 48,
    minHeight: 48,
    borderRadius: "8px",
    bgcolor: "#fff",
    boxSizing: "border-box",
    px: 2,
    fontSize: 16,
    lineHeight: "24px",
    letterSpacing: "-0.015em",
  },
  "& .MuiInputBase-input": {
    height: 24,
    p: 0,
    color: "#2E3341",
    lineHeight: "24px",
    "&::placeholder": {
      color: "#A3A3A3",
      opacity: 1,
    },
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#DDE0EA" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#C8CDD9" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2E3341" },
};
const keywordDialogExcludeSectionSx = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
};
const keywordDialogExcludeHeaderSx = {
  width: "100%",
  minHeight: 24,
};
const keywordDialogCountTagSx = {
  minWidth: 36,
  height: 24,
  px: 1,
  borderRadius: "16px",
  bgcolor: "#EEEFF0",
  color: "#2E3341",
  fontSize: 13,
  fontWeight: 500,
  lineHeight: "13px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
};
const keywordDialogExcludeInputRowSx = {
  width: "100%",
  gap: 0.5,
  height: 48,
  alignItems: "flex-start",
};
const keywordDialogExcludeAddButtonSx = {
  width: 88,
  minWidth: 88,
  height: 48,
  borderRadius: "8px",
  border: "1px solid #2E3341",
  bgcolor: "#fff",
  color: "#2E3341",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "14px",
  letterSpacing: "-0.015em",
  boxShadow: "none",
  gap: 1,
  "& .MuiButton-startIcon": {
    m: 0,
    "& svg": {
      width: 16,
      height: 16,
      strokeWidth: 2,
    },
  },
  "&:hover": { bgcolor: "#FAFAFA", borderColor: "#2E3341", boxShadow: "none" },
  "&.Mui-disabled": {
    color: "#A3A3A3",
    borderColor: "#DDE0EA",
    bgcolor: "#fff",
  },
};
const keywordDialogExcludeWellSx = (hasTerms: boolean) => ({
  width: "100%",
  minHeight: hasTerms ? 64 : 52,
  p: 2,
  borderRadius: "8px",
  border: "1px dashed #DDE0EA",
  bgcolor: "#FAFAFA",
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: hasTerms ? "flex-start" : "center",
});
const keywordDialogExcludeChipListSx = {
  width: "100%",
  flexWrap: "wrap",
  gap: 1,
  alignItems: "center",
  alignContent: "flex-start",
};
const keywordDialogExcludeChipSx = {
  height: 32,
  borderRadius: "16px",
  bgcolor: "#EAEAEA",
  color: "#2E3341",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "14px",
  "& .MuiChip-label": {
    px: 1.5,
    pr: 0.5,
  },
  "& .MuiChip-deleteIcon": {
    m: 0,
    mr: 0.5,
  },
};
const keywordDialogExcludeEmptySx = {
  color: "#6F7279",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "20px",
};
const keywordDialogEnabledRowSx = {
  gap: 1,
  width: "auto",
  height: 24,
};
const keywordDialogDisabledLabelSx = {
  color: "#6F7279",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "22px",
  letterSpacing: "-0.015em",
};
const keywordDialogEnabledLabelSx = {
  ...keywordDialogDisabledLabelSx,
  color: "#2E3341",
};

const sentimentKeywordBoxSx = {
  p: 2,
  display: "flex",
  flexDirection: "column",
  gap: 2,
  border: "1px solid #DDE0EA",
  borderRadius: "8px",
  bgcolor: "#fff",
  boxSizing: "border-box",
};

const sentimentKeywordScopeHeaderSx = {
  minHeight: 38,
  width: "100%",
};

const sentimentKeywordScopeDescSx = {
  mt: 0,
  color: "#6F7279",
  fontSize: 13,
  fontWeight: 500,
  lineHeight: "18px",
};

const sentimentModeRadioGroupSx = {
  minHeight: 40,
  gap: 3,
  alignItems: "center",
};

const notificationSettingsPanelSx = {
  p: 2,
  display: "flex",
  flexDirection: "column",
  gap: 2,
  border: "1px solid #DDE0EA",
  borderRadius: "8px",
  bgcolor: "#fff",
  boxSizing: "border-box",
};

const notificationScopeSectionSx = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  minWidth: 0,
};

const notificationPanelLabelSx = {
  color: "#2E3341",
  fontSize: 13,
  fontWeight: 600,
  lineHeight: "20px",
};

const notificationConditionGroupSx = {
  display: "flex",
  flexDirection: "column",
  gap: 0.5,
  minWidth: 0,
};

const recipientUserSectionSx = {
  display: "flex",
  flexDirection: "column",
  gap: 0.5,
  minWidth: 0,
};

const recipientPickerSx = {
  position: "relative",
  zIndex: 3,
  width: "100%",
};

const recipientInputSx = {
  "& .MuiOutlinedInput-root": {
    height: 40,
    minHeight: 40,
    borderRadius: "8px",
    bgcolor: "#fff",
    boxSizing: "border-box",
    px: 2,
    fontSize: 16,
    lineHeight: "24px",
    letterSpacing: "-0.015em",
  },
  "& .MuiInputBase-input": {
    height: 24,
    p: 0,
    color: "#2E3341",
    lineHeight: "24px",
    "&::placeholder": {
      color: "#A3A3A3",
      opacity: 1,
    },
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#DDE0EA" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#C8CDD9" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2E3341" },
};

const selectedRecipientWellSx = {
  minHeight: 64,
  p: 2,
  borderRadius: "8px",
  border: "1px dashed #DDE0EA",
  bgcolor: "#FAFAFA",
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  width: "100%",
};

const selectedRecipientListSx = {
  flexWrap: "wrap",
  gap: 1,
  minHeight: 20,
  alignItems: "center",
  alignContent: "center",
  width: "100%",
};

const selectedRecipientEmptySx = {
  width: "100%",
  color: "#6F7279",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "20px",
  textAlign: "center",
};

const selectedRecipientChipSx = {
  height: 32,
  borderRadius: "16px",
  bgcolor: "#EAEAEA",
  color: "#2E3341",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "14px",
  maxWidth: "100%",
  "& .MuiChip-label": {
    px: 1.5,
    pr: 0.5,
    minWidth: 0,
  },
  "& .MuiChip-deleteIcon": {
    m: 0,
    mr: 0.5,
  },
};

const recipientHelperRowSx = {
  minHeight: 20,
  gap: 1,
  width: "100%",
};

const recipientHelperTextSx = {
  color: "#6F7279",
  fontSize: 13,
  fontWeight: 500,
  lineHeight: "20px",
  letterSpacing: "-0.015em",
};

const recipientClearButtonSx = {
  minWidth: "auto",
  p: 0,
  color: "#DC2626",
  fontSize: 13,
  fontWeight: 600,
  lineHeight: "20px",
  "&:hover": {
    bgcolor: "transparent",
    color: "#C62828",
  },
};

const keywordPreviewSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr)" },
  gap: 1,
  minWidth: 0,
};

const customKeywordGridSx = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: 1,
};

const sentimentKeywordEditorSx = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
  minWidth: 0,
};

const negativeKeywordPanelSx = {
  p: 2,
  borderRadius: "8px",
  border: "1px solid #DDE0EA",
  bgcolor: "#fff",
  minWidth: 0,
  minHeight: 134,
  boxSizing: "border-box",
};

const negativeKeywordSectionTitleSx = {
  color: "#2E3341",
  fontSize: 13,
  fontWeight: 600,
  lineHeight: "20px",
};

const negativeKeywordCountChipSx = {
  height: 24,
  borderRadius: "16px",
  bgcolor: "#FFF2F2",
  color: "#E32329",
  fontSize: 13,
  fontWeight: 500,
  lineHeight: "13px",
  "& .MuiChip-label": {
    px: 1,
  },
};

const negativeKeywordInputRowSx = {
  gap: 1,
  width: "100%",
};

const negativeKeywordInputSx = {
  ...recipientInputSx,
  flex: "1 1 auto",
};

const negativeKeywordAddIconSx = {
  width: 16,
  height: 16,
  color: "#2E3341",
};

const negativeKeywordAddButtonSx = {
  height: 40,
  minWidth: 76,
  px: 2,
  borderRadius: "8px",
  border: "1px solid #2E3341",
  bgcolor: "#fff",
  color: "#2E3341",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "14px",
  letterSpacing: "-0.015em",
  boxShadow: "none",
  "&:hover": {
    bgcolor: "#FAFAFA",
    borderColor: "#2E3341",
  },
  "&.Mui-disabled": {
    borderColor: "#DDE0EA",
    color: "#A3A3A3",
  },
};

const negativeKeywordSelectedWellSx = {
  ...selectedRecipientWellSx,
  minHeight: 52,
};

const negativeKeywordTitleSx = {
  color: "#000",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "22px",
};
const negativeKeywordChipListSx = {
  mt: 1,
  flexWrap: "wrap",
  gap: 0.5,
  minHeight: 136,
  alignItems: "flex-start",
  alignContent: "flex-start",
};
const negativeKeywordEmptySx = {
  width: "100%",
  color: "#6F7279",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "20px",
  textAlign: "center",
};

const sentimentKeywordChipListSx = {
  flexWrap: "wrap",
  gap: 1,
  minHeight: 20,
  alignItems: "center",
  alignContent: "center",
  width: "100%",
};

const smallChipSx = {
  height: 24,
  borderRadius: "20px",
  bgcolor: "#EEEFF0",
  color: "rgba(46, 51, 65, 0.6)",
  fontSize: 13,
  fontWeight: 500,
};

const selectedTinyChipSx = {
  ...smallChipSx,
  bgcolor: "#F0F4FF",
  color: "#5D88F1",
};

const choiceCardSx = (selected: boolean, disabled?: boolean) => ({
  position: "relative",
  p: 2,
  minHeight: 124,
  height: "100%",
  borderRadius: "8px",
  border: `1px solid ${selected ? "#5D88F1" : "#DDE0EA"}`,
  bgcolor: selected ? "#F0F4FF" : "#fff",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  justifyContent: selected ? "center" : "flex-start",
  alignItems: "flex-start",
  gap: 1,
  cursor: disabled ? "not-allowed" : "pointer",
});

const choiceRadioSx = (selected: boolean) => ({
  width: 16,
  height: 16,
  flex: "0 0 auto",
  borderRadius: "50%",
  bgcolor: selected ? "#5D88F1" : "#D4D4D4",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const choiceRadioDotSx = {
  width: 8,
  height: 8,
  borderRadius: "50%",
  bgcolor: "#fff",
};

const choiceTextSx = (disabled?: boolean) => ({
  width: "100%",
  opacity: disabled ? 0.6 : 1,
});

const choiceTitleSx = {
  color: "#2E3341",
  fontSize: 16,
  fontWeight: 600,
  lineHeight: "24px",
  letterSpacing: "-0.015em",
};

const choiceDescSx = {
  color: "#6F7279",
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "22px",
  letterSpacing: "-0.015em",
};

const choiceBadgeSx = {
  position: "absolute",
  top: 16,
  right: 16,
  height: 24,
  minWidth: 88,
  px: 1.5,
  borderRadius: "16px",
  bgcolor: "#EEEFF0",
  color: "rgba(46, 51, 65, 0.6)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "14px",
};

const negativeChipSx = {
  height: 24,
  borderRadius: "16px",
  bgcolor: "#FFF2F2",
  color: "#E32329",
  fontSize: 13,
  fontWeight: 500,
  lineHeight: "13px",
  "& .MuiChip-label": {
    px: 1,
  },
};

const excludeTermChipSx = {
  ...smallChipSx,
  bgcolor: "#ecfeff",
  color: "#0e7490",
};

const successChipSx = {
  ...smallChipSx,
  bgcolor: "#EAF7EE",
  color: "#15A935",
};

const warningChipSx = {
  ...smallChipSx,
  bgcolor: "#FEF3EB",
  color: "#D04900",
};

const primaryButtonSx = {
  height: 48,
  minHeight: 48,
  borderRadius: "8px",
  bgcolor: "#2E3341",
  color: "#fff",
  fontSize: 16,
  fontWeight: 600,
  lineHeight: "16px",
  letterSpacing: "-0.015em",
  boxShadow: "none",
  "&:hover": { bgcolor: "#1F2532", boxShadow: "none" },
};

const outlineButtonSx = {
  borderRadius: "8px",
  color: "#2E3341",
  border: "1px solid #DDE0EA",
  bgcolor: "#fff",
  fontSize: 14,
  fontWeight: 600,
  "&:hover": { borderColor: "#C7CCD9", bgcolor: "#FAFAFA" },
};

const miniOutlineButtonSx = {
  ...outlineButtonSx,
  height: 32,
  minHeight: 32,
  px: 1.5,
  py: 0,
  fontSize: 14,
  lineHeight: "14px",
  letterSpacing: "-0.015em",
};

const compactActionButtonSx = {
  height: 40,
  minHeight: 40,
  px: 2,
  py: 0,
  gap: 0.5,
  fontSize: 14,
  lineHeight: "14px",
};

const headerActionButtonSx = {
  ...outlineButtonSx,
  ...compactActionButtonSx,
  "& .MuiButton-startIcon": { m: 0 },
};

const keywordDialogActionOutlineButtonSx = {
  ...outlineButtonSx,
  ...compactActionButtonSx,
  width: 80,
  minWidth: 80,
};

const keywordDialogActionPrimaryButtonSx = {
  ...primaryButtonSx,
  ...compactActionButtonSx,
  width: "auto",
  minWidth: 95,
  px: 2,
  fontSize: 14,
  lineHeight: "14px",
};

const dangerTextButtonSx = {
  minWidth: "auto",
  px: 0.6,
  color: "#DC2626",
  fontSize: 13,
  fontWeight: 600,
  lineHeight: "20px",
  "&:hover": { bgcolor: "#fff1f2" },
  "&.Mui-disabled": { color: "#cbd5e1" },
};

const textFieldSx = {
  "& .MuiInputBase-root": {
    minHeight: 48,
    borderRadius: "8px",
    bgcolor: "#fff",
    fontSize: 16,
    lineHeight: "24px",
    letterSpacing: "-0.015em",
  },
  "& .MuiInputBase-input": { py: 1.5 },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#DDE0EA" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2E3341" },
  "& .MuiFormHelperText-root": { mx: 0, mt: 0.5, color: "#6F7279", fontSize: 13, fontWeight: 500, lineHeight: "20px" },
};

const scheduleFieldSx = {
  "& .MuiInputBase-root": {
    height: 40,
    minHeight: 40,
    borderRadius: "8px",
    bgcolor: "#fff",
    fontSize: 14,
    lineHeight: "20px",
    letterSpacing: "-0.015em",
  },
  "& .MuiInputBase-input": { py: 1, px: 2 },
  "& .MuiSelect-select": {
    py: 1,
    px: 2,
    minHeight: "20px !important",
    display: "flex",
    alignItems: "center",
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#DDE0EA" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2E3341" },
  "& .MuiSelect-icon": { color: "#2E3341" },
  "& .MuiFormHelperText-root": { mx: 0, mt: 0.5, color: "#6F7279", fontSize: 13, fontWeight: 500, lineHeight: "20px" },
};

export default MonitoringCreatePage;
